import React, { useEffect, useRef, useState, useCallback } from "react"
import "./CourseList.scss"
import CourseApi from "Api/CourseApi"
import {
  Button,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Table,
  Spinner,
  Input,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import { useNavigate } from "react-router-dom"
import DataTableLoader from "SkeletonLoader/DataTableLoader"
import { Toast } from 'primereact/toast'
import { OverlayPanel } from "primereact/overlaypanel"
import LogsSidebar from "components/LogsSidebar/LogsSidebar"
import LogsApi from "Api/LogsApi"

const CourseList = () => {
  document.title = "Course"

  const accessToken = localStorage.getItem("accessToken")
  const [courseDetails, setCourseDetails] = useState([])
  const [institutions, setInstitutions] = useState([])
  const [programs, setPrograms] = useState([])
  const [page, setPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [limit, setLimit] = useState(10)
  const navigate = useNavigate("")
  const os = useRef(null)

  const userData = JSON.parse(localStorage.getItem("userData"))
  const [loading, setLoading] = useState(true)
  const [addCourseLoader, setAddCourseLoader] = useState(false)
  const [FilteredCourseDetails, setFilteredCourseDetails] = useState([])
  const [filterStatus, setFilterStatus] = useState("all")
  const toast = useRef(null)
  const [selectedCourses, setSelectedCourses] = useState([])
  const [logsVisible, setLogsVisible] = useState(false)
  const [logs, setLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [allLogs, setAllLogs] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const searchTimeoutRef = useRef(null)

  const getAllCourseDetails = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        limit: limit,
        page: page,
      },
    }

    CourseApi.getAllCourse(config)
      .then(response => {
        if (response.data.status) {
          setLoading(false)
          setCourseDetails(response.data.data.courses)
          setTotalRecords(response.data.data.pagination.totalRecords)
        }
      })
      .catch(error => {
        setLoading(false)
        toast.current.show({severity:'error', summary: 'Error', detail: error.response?.data?.message || 'Failed to fetch courses', life: 3000});
      })
  }

  const debouncedSearch = useCallback((query) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (!query || query.trim() === "") {
        getAllCourseDetails()
        setInstitutions([])
        setPrograms([])
        return
      }

      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          query: query,
          limit: limit,
          page: page,
        },
      }

      try {
        const response = await CourseApi.searchCourseAndProgram(config)
        if (response.data.status) {
          setCourseDetails(response.data.data.courses || [])
          setInstitutions(response.data.data.institutions || [])
          setPrograms(response.data.data.programs || [])
          setFilteredCourseDetails(response.data.data.courses || [])
          setTotalRecords(response.data.data.courses?.length || 0)
        } else {
          setCourseDetails([])
          setInstitutions([])
          setPrograms([])
          setFilteredCourseDetails([])
          setTotalRecords(0)
          toast.current.show({
            severity: "info",
            summary: "No Results",
            detail: response.data.message || "No results found matching your search",
            life: 3000,
          })
        }
      } catch (error) {
        setCourseDetails([])
        setInstitutions([])
        setPrograms([])
        setFilteredCourseDetails([])
        setTotalRecords(0)
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Failed to search",
          life: 3000,
        })
      }
    }, 800)
  }, [accessToken, page, limit])

  const handleSearch = e => {
    const value = e.target.value
    setSearchQuery(value)
    setPage(1) // Reset to first page when searching
    debouncedSearch(value)
  }

  // Update useEffect to handle search pagination
  useEffect(() => {
    if (searchQuery.trim() !== "") {
      debouncedSearch(searchQuery)
    } else {
      getAllCourseDetails()
    }
  }, [page, limit, searchQuery])

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    let filteredData = courseDetails

    if (filterStatus !== "all") {
      filteredData = filteredData.filter(
        course => course.verifiedStatus === filterStatus
      )
    }

    setFilteredCourseDetails(filteredData)
  }, [filterStatus, courseDetails])

  const breadcrumbItems = [
    { label: "Pixwik", link: "#" },
    { label: "Course", link: "#" },
    { label: "Course List", link: "#" },
  ]

  const totalPages = Math.ceil(totalRecords / limit)

  const uploadbtn = useRef()

  const handleUploadButtonClick = () => {
    uploadbtn.current.click()
  }
  const [excelLoader, setExcelLoader] = useState(false)

  const handleFileChange = event => {
    setExcelLoader(true)

    const file = event.target.files[0]
    if (!file) return
    const formData = new FormData()
    formData.append("file", file)

    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    CourseApi.uploadCourseByExcel(formData, config)
      .then(res => {
        getAllCourseDetails()
        event.target.value = ""
        setExcelLoader(false)
        toast.current.show({severity:'success', summary: 'Success', detail: res.data.message, life: 3000});
      })
      .catch(err => {
        event.target.value = ""
        setExcelLoader(false)
        toast.current.show({severity:'error', summary: 'Error', detail: err.response.data.message, life: 3000});
      })
  }

  const handleCourseClick = () => {
    setAddCourseLoader(true)
    setTimeout(() => {
      setAddCourseLoader(false)
      navigate("/addCourse")
    }, 500)
  }

  const toggleSelectAll = () => {
    if (selectedCourses.length === FilteredCourseDetails.length) {
      setSelectedCourses([])
    } else {
      setSelectedCourses(FilteredCourseDetails.map(course => course._id))
    }
  }

  const fetchLogs = async () => {
    setLogsLoading(true)
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
      const response = await LogsApi.getLogsByServiceName("course-service", config)
      const sortedLogs = response.data.logs.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      )
      setAllLogs(sortedLogs)
      setLogs(sortedLogs)
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch logs",
        life: 3000,
      })
    } finally {
      setLogsLoading(false)
    }
  }

  const handleLogsClick = () => {
    setLogsVisible(true)
    fetchLogs()
  }

  const handleFilterChange = (level) => {
    if (level === 'all') {
      setLogs(allLogs);
    } else {
      setLogs(allLogs.filter(log => log.eventType.toLowerCase() === level));
    }
  };

  const toggleSelection = courseId => {
    setSelectedCourses(prevCourses =>
      prevCourses.includes(courseId)
        ? prevCourses.filter(id => id !== courseId)
        : [...prevCourses, courseId],
    )
  }

  const handleUpdateStatus = (status) => {
    if (selectedCourses.length === 0) {
      toast.current.show({severity:'warn', summary: 'Warning', detail: 'Please select at least one Course to update.', life: 3000});
      return
    }

    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
    const payload = {
      courseIds: selectedCourses,
      newStatus: status
    }

    CourseApi.updateCourseStatus(payload, config)
      .then(response => {
        if (response.data.status) {
          toast.current.show({severity:'success', summary: 'Success', detail: response.data.message, life: 3000});
          getAllCourseDetails()
          setSelectedCourses([])
        }
      })
      .catch(error => {
        toast.current.show({severity:'error', summary: 'Error', detail: error.response?.data?.message || 'Failed to update Course status.', life: 3000});
      })
  }

  return (
    <>
      <LogsSidebar 
        visible={logsVisible}
        onHide={() => setLogsVisible(false)}
        logs={logs}
        loading={logsLoading}
        onRefresh={fetchLogs}
        onFilterChange={handleFilterChange}
        allLogs={allLogs}
      />
      <Toast ref={toast} />
      <Row>
        <Col xs="12">
          <div className="university-list-header d-flex  align-items-center justify-content-between">
            <CustomBreadCrumb data={breadcrumbItems} pageTitle="Course" />
            <div className="d-flex align-items-center gap-3">
              {selectedCourses.length === 0 && (
                <>
                  <Input
                    type="text"
                    placeholder="Search Courses, Programs, or Institutions"
                    className="form-control"
                    style={{ width: "300px" }}
                    value={searchQuery}
                    onChange={handleSearch}
                  />
                  <input
                    type="file"
                    ref={uploadbtn}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  <Button
                    type="button"
                    color="success"
                    className="waves-effect"
                    onClick={handleUploadButtonClick}
                  >
                    {excelLoader ? (
                      <Spinner size="sm" />
                    ) : (
                      <i className="mdi mdi-microsoft-excel" />
                    )}
                  </Button>

                  <Button
                    type="button"
                    color="secondary"
                    className="waves-effect"
                    onClick={handleCourseClick}
                  >
                    Add Course
                  </Button>

                  <Button
                    type="button"
                    color="info"
                    className="waves-effect"
                    style={{ minWidth: "100px" }}
                    onClick={handleLogsClick}
                  >
                    <i className="mdi mdi-file-document-outline me-1" />
                    Logs
                  </Button>
                </>
              )}

              {selectedCourses.length > 0 && (
                <>
                  <Button
                    type="button"
                    color="primary"
                    className="waves-effect"
                    onClick={() => handleUpdateStatus("approve")}
                  >
                    Approve
                  </Button>
                  <Button
                    type="button"
                    color="danger"
                    className="waves-effect"
                    onClick={() => handleUpdateStatus("reject")}
                  >
                    Reject
                  </Button>
                </>
              )}
            </div>
          </div>

          {searchQuery && (institutions.length > 0 || programs.length > 0) && (
            <div className="search-results mt-3">
              {institutions.length > 0 && (
                <div className="mb-3">
                  <h5>Institutions ({institutions.length})</h5>
                  <div className="d-flex flex-wrap gap-2">
                    {institutions.map(institution => (
                      <div key={institution._id} className="search-item p-2 border rounded">
                        <div className="fw-bold">{institution.institutionName}</div>
                        <div className="small text-muted">{institution.institutionCode}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {programs.length > 0 && (
                <div className="mb-3">
                  <h5>Programs ({programs.length})</h5>
                  <div className="d-flex flex-wrap gap-2">
                    {programs.map(program => (
                      <div key={program._id} className="search-item p-2 border rounded">
                        <div className="fw-bold">{program.programName}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="course-data-table table-responsive mt-4 mb-3 ">
            {loading ? (
              <DataTableLoader cols={10} />
            ) : (
              <Table className="table mb-0">
                <thead className="table-light">
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onClick={toggleSelectAll}
                        checked={
                          selectedCourses.length ===
                          FilteredCourseDetails.length
                        }
                      />
                    </th>
                    <th>#</th>
                    <th>Course Name</th>
                    <th>Duration</th>
                    <th>TuitionFee</th>
                    <th>Eligibility Criteria</th>
                    <th>Total Seats</th>
                    <th>
                      <span className="d-flex align-items-center gap-2 justify-content-center">
                        <span>Verified Status</span>
                        <span
                          onClick={e => os.current.toggle(e)}
                          style={{ cursor: "pointer" }}
                        >
                          <i className="mdi mdi-filter" />
                        </span>
                      </span>
                      <OverlayPanel ref={os}>
                        <Input
                          type="select"
                          value={filterStatus}
                          onChange={e => {
                            setFilterStatus(e.target.value)
                            os.current.hide()
                          }}
                          style={{ width: "150px" }}
                        >
                          <option value="all">All</option>
                          <option value="approve">Approved</option>
                          <option value="reject">Rejected</option>
                          <option value="pending">Pending</option>
                        </Input>
                      </OverlayPanel>
                    </th>
                    <th>Edit</th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {FilteredCourseDetails.map((course, index) => (
                    <tr key={course._id} className="university-table-row">
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedCourses.includes(course._id)}
                          onClick={() => {
                            toggleSelection(course._id)
                          }}
                        />
                      </td>
                      <th scope="row">{(page - 1) * limit + index + 1}</th>
                      <td>{course.courseName}</td>
                      <td>{course.duration}</td>
                      <td>{course.tuitionFee}</td>
                      <td>{course.eligibilityCriteria}</td>
                      <td>{course.totalSeats}</td>

                      <td>
                        <span
                          className={`status ${course.verifiedStatus.toLowerCase()}`}
                        >
                          {course.verifiedStatus === "approve"
                            ? "Approved"
                            : course.verifiedStatus === "reject"
                              ? "Rejected"
                              : "Pending"}
                        </span>
                      </td>

                      <td>
                        <span
                          className="edit-course"
                          onClick={() => {
                            navigate(`/edit-course/${course?._id}`, {
                              state: course,
                            })
                          }}
                        >
                          <i className="ion ion-md-create" />
                        </span>
                      </td>

                      <td>
                        <span
                          onClick={() => {
                            navigate(`/view-course/${course?._id}`)
                          }}
                          className="view-course"
                        >
                          <i className="ion ion-md-eye" />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
          <div className="pagination-container d-flex align-items-center justify-content-end">
            <Pagination>
              <PaginationItem disabled={page <= 1}>
                <PaginationLink previous onClick={() => setPage(page - 1)} />
              </PaginationItem>
              {totalPages > 0 && [...Array(Math.min(3, totalPages))].map((_, index) => (
                <PaginationItem key={index} active={index + 1 === page}>
                  <PaginationLink onClick={() => setPage(index + 1)}>
                    {index + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}

              {totalPages > 6 && page > 3 && page < totalPages - 2 && (
                <PaginationItem disabled>
                  <PaginationLink>...</PaginationLink>
                </PaginationItem>
              )}

              {totalPages > 6 && page > 3 && page < totalPages - 2 && (
                <PaginationItem active>
                  <PaginationLink onClick={() => setPage(page)}>
                    {page}
                  </PaginationLink>
                </PaginationItem>
              )}

              {totalPages > 6 && page < totalPages - 2 && (
                <PaginationItem disabled>
                  <PaginationLink>...</PaginationLink>
                </PaginationItem>
              )}

              {totalPages > 3 && totalPages > 0 &&
                [...Array(Math.min(3, totalPages))].map((_, index) => {
                  const pageNumber = totalPages - 2 + index
                  return (
                    <PaginationItem
                      key={pageNumber}
                      active={pageNumber === page}
                    >
                      <PaginationLink onClick={() => setPage(pageNumber)}>
                        {pageNumber}
                      </PaginationLink>
                    </PaginationItem>
                  )
                })}
              <PaginationItem disabled={page >= totalPages}>
                <PaginationLink next onClick={() => setPage(page + 1)} />
              </PaginationItem>
            </Pagination>
          </div>
        </Col>
      </Row>
    </>
  )
}

export default CourseList
